import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { CatalogosLote, CotizacionAduana } from './tipos';

/**
 * Valor en aduana cotizado por el agente aduanal (arquitectura 2.15, sección 7): catálogo
 * privado del lotero por año, modelo, versión, tipo y región, con vigencia de 30 días por omisión.
 * Con cotización vigente, el dictamen y el semáforo usan ese valor; sin ella, salen en «Revisar».
 */
export function CotizacionesAduana() {
  const api = useApi();
  const permitido = usePuede();
  const lista = useConsulta<CotizacionAduana[]>('/sourcing/importacion/cotizaciones');
  const { datos: catalogos } = useConsulta<CatalogosLote>('/sourcing/subastas/catalogos');
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);
  const hoy = fechaHoraLocal(new Date()).slice(0, 10);
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim();
    setError(null);
    try {
      await api('POST', '/sourcing/importacion/cotizaciones', {
        agente: { nombre: t('agente') },
        anio: Number(t('anio')),
        marca: t('marca'),
        modelo: t('modelo'),
        ...(t('version') && { version: t('version') }),
        tipoImportacion: t('tipoImportacion'),
        region: t('region'),
        valorAduanaMxn: t('valorAduanaMxn'),
        fecha: t('fecha'),
        vigenciaDias: Number(t('vigenciaDias') || 30),
        ...(t('nota') && { nota: t('nota') }),
      });
      setAbierto(false);
      lista.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const vigente = (c: CotizacionAduana) => c.vigenteHasta >= hoy;
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Valor en aduana</h1>
          <div className="subtitulo">
            Lo que tu agente aduanal cotiza por año, modelo y versión; se reutiliza mientras esté
            vigente
          </div>
        </div>
        <div className="relleno" />
        {permitido('importacion.abrir') && (
          <button className="boton primario" onClick={() => setAbierto((x) => !x)}>
            {abierto ? 'Cancelar' : 'Registrar cotización'}
          </button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {abierto && (
        <form className="seccion cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>Agente aduanal</span>
            <input name="agente" required maxLength={120} />
          </label>
          <label className="campo">
            <span>Año</span>
            <input name="anio" type="number" required min={1900} max={2100} />
          </label>
          <label className="campo">
            <span>Marca</span>
            <input name="marca" required maxLength={40} />
          </label>
          <label className="campo">
            <span>Modelo</span>
            <input name="modelo" required maxLength={60} />
          </label>
          <label className="campo">
            <span>Versión (opcional)</span>
            <input name="version" maxLength={80} />
          </label>
          <label className="campo">
            <span>Vía de importación</span>
            <select name="tipoImportacion" defaultValue="definitiva_resto">
              {(catalogos?.tiposImportacion ?? []).map((t) => (
                <option key={t.clave} value={t.clave}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Región</span>
            <select name="region" defaultValue="resto">
              <option value="frontera">Región fronteriza</option>
              <option value="resto">Resto del país</option>
            </select>
          </label>
          <label className="campo">
            <span>Valor en aduana (MXN)</span>
            <input
              name="valorAduanaMxn"
              required
              inputMode="decimal"
              pattern="\d{1,12}(\.\d{1,2})?"
            />
          </label>
          <label className="campo">
            <span>Fecha de la cotización</span>
            <input name="fecha" type="date" required defaultValue={hoy} />
          </label>
          <label className="campo">
            <span>Vigencia (días)</span>
            <input name="vigenciaDias" type="number" min={1} max={365} defaultValue={30} />
          </label>
          <label className="campo">
            <span>Nota</span>
            <input name="nota" maxLength={500} />
          </label>
          <div className="acciones">
            <button className="boton primario">Guardar</button>
          </div>
        </form>
      )}
      <section className="seccion">
        {lista.error && <p className="error">{lista.error}</p>}
        {lista.datos && !lista.datos.length ? (
          <p className="vacio">
            Sin cotizaciones. Sin cotización vigente, el dictamen estima con lo pagado y sale en
            «Revisar».
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Auto</th>
                <th>Vía</th>
                <th className="num">Valor en aduana</th>
                <th>Agente</th>
                <th>Vigencia</th>
              </tr>
            </thead>
            <tbody>
              {(lista.datos ?? []).map((c) => (
                <tr key={c.id} style={{ opacity: vigente(c) ? 1 : 0.5 }}>
                  <td>
                    {c.anio} {c.marca} {c.modelo}
                    {c.version ? ` ${c.version}` : ''}
                  </td>
                  <td>
                    {c.tipoImportacion} · {c.region}
                  </td>
                  <td className="num">{mxn(c.valorAduanaMxn)}</td>
                  <td>{c.agente.nombre}</td>
                  <td>
                    {fechaCorta(c.fecha)} → {fechaCorta(c.vigenteHasta)}{' '}
                    {vigente(c) ? (
                      <span className="etiqueta exito">vigente</span>
                    ) : (
                      <span className="etiqueta">vencida</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
